import { query } from '@zealy/contracts/src/utils';
import { extractErrorMessage } from '@zealy/utils';
import { apiV2 } from '../services';
import { api } from '../services/api';
import { config } from '../services/config';
import { AppError } from '../types';
class HttpError extends Error {
    response;
    constructor(response, data) {
        super(`HTTP Error ${response.status}`);
        this.name = 'HttpError';
        this.response = Object.assign(response, { _data: data });
    }
}
export const generateMagicQuest = async (subdomain, body) => {
    const res = await apiV2.magicQuest.generate({
        params: {
            subdomain,
        },
        body,
    });
    if (res.status === 200) {
        return res.body;
    }
    else {
        // handle error here
        throw new Error('getAdminQuestboardWithClaim failed');
    }
};
export const generateMagicQuestSuggestions = async (subdomain, body) => {
    const res = await apiV2.magicQuest.generateSuggestions({
        params: {
            subdomain,
        },
        body,
    });
    if (res.status === 200) {
        return res.body;
    }
    else {
        // handle error here
        throw new Error('generateMagicQuestSuggestions failed');
    }
};
export const getQuestboardModule = async (subdomain, moduleId, filters) => api.get(`/communities/${subdomain}/questboard/v2/${moduleId}`, {
    query: {
        filters,
    },
});
export const getQuestBoard = async (subdomain, filters) => api.get(`/communities/${subdomain}/questboard/v2`, {
    query: {
        filters,
    },
});
export const testClaimQuest = (subdomain, questId, tasks) => {
    // We use the old testClaimQuest endpoint to test the quest claim
    return (taskValues) => api.post(`/communities/${subdomain}/quests/v2/${questId}/test-claim`, {
        body: {
            tasks,
            taskValues,
        },
    });
};
export const claimQuest = (subdomain, questId) => {
    return async (params) => {
        const response = await fetch(`${config.baseURL}/communities/${subdomain}/quests/v2/${questId}/claim`, {
            credentials: 'include',
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                // TODO this is quickfix for rate limiting by community in CloudFront
                'X-Zealy-Subdomain': subdomain,
            },
            body: JSON.stringify(params),
        });
        if (response.status === 202) {
            const operationLocation = response.headers.get('Operation-Location');
            if (!operationLocation) {
                console.error('Missing Operation-Location header for claim status polling');
                return (await response.json());
            }
            const maxRetries = 10;
            let retryCount = 0;
            let delay = 1500;
            while (retryCount < maxRetries) {
                const jitter = Math.random() * 1000 - 500;
                await new Promise(resolve => setTimeout(resolve, delay + jitter));
                const statusResponse = await fetch(operationLocation, {
                    method: 'GET',
                    credentials: 'include',
                });
                if (!statusResponse.ok) {
                    if (statusResponse.status === 400) {
                        const errorData = await statusResponse.json();
                        throw new HttpError(statusResponse, errorData);
                    }
                    retryCount++;
                    delay = Math.min(delay * 2, 8000);
                    continue;
                }
                const data = (await statusResponse.json());
                if (data.status !== 'inReview') {
                    return data;
                }
                retryCount++;
                delay = Math.min(delay * 2, 8000);
            }
            console.error('Claim quest polling exceeded max retries');
            return (await response.json());
        }
        else {
            if (!response.ok) {
                // Consume the response body and throw an HttpError with response._data populated
                const errorData = await response.json();
                throw new HttpError(response, errorData);
            }
            return (await response.json());
        }
    };
};
export const getQuest = (subdomain, questId, options) => api.get(`/communities/${subdomain}/quests/v2/${questId}`, {
    ...options,
});
/**
 * Public/unauthenticated version of getQuest that skips cookie forwarding.
 * Use this for ISR/cached routes like generateMetadata where you don't need auth context.
 */
export const getQuestPublic = async (subdomain, questId, opts) => {
    const fetchOptions = {
        headers: {
            ...config.headers,
        },
    };
    if (opts?.revalidate !== undefined || opts?.tags) {
        fetchOptions.next = { revalidate: opts?.revalidate, tags: opts?.tags };
    }
    const response = await fetch(`${config.baseURL}/communities/${subdomain}/quests/v2/${questId}`, fetchOptions);
    if (!response.ok) {
        throw new Error(`getQuestPublic failed: status=${response.status}`);
    }
    return response.json();
};
export const getQuests = (subdomain) => api.get(`/communities/${subdomain}/quests`, {
    query: {
        version: 'v2',
    },
});
export const createQuest = async ({ subdomain, data }, generated) => {
    const res = await apiV2.quest.createQuest({
        params: { subdomain },
        body: data,
        query: generated ? { generated: 'true' } : undefined,
    });
    if (res.status === 200) {
        return res.body;
    }
    else {
        // handle error here
        throw new Error(`createQuest failed status=${res.status}, message=${extractErrorMessage(res.body)}`);
    }
};
export const updateQuest = ({ subdomain, questId, data }) => api.patch(`/communities/${subdomain}/quests/v2/${questId}`, {
    body: JSON.stringify(data),
});
export const updateQuestV2 = async ({ subdomain, questId, data }) => {
    const res = await apiV2.quest.updateQuest({
        params: { subdomain, questId },
        body: data,
    });
    if (res.status === 200) {
        return res.body;
    }
    if (res.status === 400) {
        throw new AppError(res.body.message, res.body.context);
    }
    else {
        throw new AppError('updateQuestV2 failed');
    }
};
export const updateQuests = async ({ subdomain, questIds, data }) => {
    const res = await apiV2.quest.patchQuests({
        params: {
            subdomain,
        },
        body: {
            questsIds: questIds,
            ...data,
        },
    });
    if (res.status === 200) {
        return res.body;
    }
    else {
        throw new Error('updateQuests failed');
    }
};
export const deleteQuest = ({ subdomain, questId }) => api.delete(`/communities/${subdomain}/quests/${questId}`);
export const getAdminQuest = (subdomain, questId, options) => api.get(`/communities/${subdomain}/quests/v2/${questId}/admin`, options);
export const getAdminQuestboard = (subdomain, options) => {
    return api.get(`/communities/${subdomain}/questboard/v2/admin`, {
        ...options,
        query: {
            ...query,
            filters: options?.query?.filters ? JSON.stringify(options?.query?.filters) : undefined,
        },
    });
};
export const getAdminQuestboardWithClaim = async (subdomain, startDate) => {
    const res = await apiV2.leaderboardSprint.listQuests({
        params: {
            subdomain,
        },
        query: {
            startDate,
        },
    });
    if (res.status === 200) {
        return res.body.data;
    }
    else {
        // handle error here
        throw new Error('getAdminQuestboardWithClaim failed');
    }
};
export const getTemplates = (subdomain, options) => api.get(`/communities/${subdomain}/templates/v2`, options);
export const listTemplateQuests = async (subdomain, options) => {
    const res = await apiV2.template.listTemplateQuests({
        ...options,
        params: {
            subdomain,
        },
    });
    if (res.status === 200) {
        return res.body.data;
    }
    else {
        // handle error here
        throw new Error('listTemplateQuests failed');
    }
};
export const listTemplateModules = async (subdomain, options) => {
    const res = await apiV2.template.listTemplateModules({
        ...options,
        params: {
            subdomain,
        },
    });
    if (res.status === 200) {
        return res.body.data;
    }
    else {
        // handle error here
        throw new Error('listTemplateModules failed');
    }
};
export const getTemplateQuest = async (params, options) => {
    const res = await apiV2.template.getTemplateQuest({
        ...options,
        params,
    });
    if (res.status === 200) {
        return res.body;
    }
    else {
        // handle error here
        throw new Error('getTemplateQuest failed');
    }
};
export const getTemplate = (subdomain, templateId, options) => api.get(`/communities/${subdomain}/templates/v2/${templateId}`, options);
export const reorderQuests = async ({ subdomain, payload, }) => {
    const res = await apiV2.quest.reorderQuests({
        params: { subdomain },
        body: payload,
    });
    if (res.status === 200) {
        return res.body;
    }
    else {
        throw new AppError('reorderQuests failed');
    }
};
export const getQuestResult = (subdomain, questId, options) => api.get(`/communities/${subdomain}/quests/v2/${questId}/result`, options);
export const getTaskResult = async (subdomain, questId, taskId, options) => {
    const data = await api.get(`/communities/${subdomain}/quests/v2/${questId}/tasks/${taskId}/result`, options);
    const receivedFullResult = data?.length >= Number(options?.query?.limit || 10);
    return {
        results: data,
        hasNextPage: receivedFullResult,
        nextPage: receivedFullResult ? Number(options?.query?.page || 1) + 1 : undefined,
    };
};
export const getTelegramMetadata = (inviteLink) => api.get(`/external/telegram/group/${encodeURIComponent(inviteLink)}`);
export const getDiscordMetadata = (inviteLink) => api.get(`/external/discord/${encodeURIComponent(inviteLink)}`);
export const getQuestRewards = async (subdomain, questId) => {
    const res = await apiV2.reward.get({
        params: { subdomain, questId },
    });
    if (res.status === 200) {
        return res.body.data;
    }
    else {
        // handle error here
        throw new Error('getQuestRewards failed');
    }
};
export const listTaskValidations = async (subdomain, questId, taskId, sortedBy) => {
    const res = await apiV2.quest.listTaskValidations({
        params: { subdomain, questId, taskId },
        query: {
            sortedBy,
        },
    });
    if (res.status === 200) {
        return res.body;
    }
    else {
        // handle error here
        throw new Error('listTaskValidations failed');
    }
};
export const updateTokenRewardTransaction = async ({ id, subdomain, data, }) => {
    const res = await apiV2.tokenReward.updateTokenRewardTransaction({
        params: { subdomain, tokenRewardId: id },
        body: data,
    });
    if (res.status === 200) {
        return res.body;
    }
    if (res.status === 400) {
        throw new AppError(res.body.message, res.body.context);
    }
    else {
        throw new AppError('updateTokenRewardTransaction failed');
    }
};
export const updateTokenRewardsTransaction = async ({ subdomain, data, }) => {
    const res = await apiV2.tokenReward.updateTokenRewardsTransaction({
        params: { subdomain },
        body: data,
    });
    if (res.status === 200) {
        return res.body;
    }
    if (res.status === 400) {
        throw new AppError(res.body.message, res.body.context);
    }
    throw new AppError('updateTokenRewardsTransaction failed');
};
export const getQuestVoteFeed = async ({ subdomain, questId, rewardId, pagination, }) => {
    const res = await apiV2.quest.getQuestVoteFeed({
        params: { subdomain, questId, rewardId },
        query: { limit: pagination?.limit, cursor: pagination?.cursor },
    });
    if (res.status === 200) {
        return res.body;
    }
    else {
        throw new Error('getQuestVoteFeed failed');
    }
};
export const voteEvent = async (params, body) => {
    const res = await apiV2.quest.createVoteEvents({ params, body });
    if (res.status === 201) {
        return res.body;
    }
    throw new AppError('voteEvent failed');
};
export const listRecommendedQuests = async (page = 0, limit = 10) => {
    const res = await apiV2.quest.listRecommendedQuests({
        query: { page, limit },
    });
    if (res.status === 200) {
        return res.body;
    }
    throw new AppError('listRecommendedQuests failed');
};
export const listCreatorQuests = async (cursor, limit = 10) => {
    const res = await apiV2.quest.listCreatorQuests({
        query: { cursor, limit },
    });
    if (res.status === 200) {
        return res.body;
    }
    throw new AppError('listCreatorQuests failed');
};
export const getQuestVoteResults = async ({ subdomain, questId, rewardId, pagination, }) => {
    const res = await apiV2.quest.getQuestVoteResults({
        params: { subdomain, questId, rewardId },
        query: { limit: pagination?.limit, cursor: pagination?.cursor },
    });
    if (res.status === 200) {
        return res.body;
    }
    else {
        throw new Error('getQuestVoteResults failed');
    }
};
export const hasVoteQuest = async (subdomain) => {
    const res = await apiV2.quest.hasVoteQuest({
        params: { subdomain },
    });
    if (res.status === 200) {
        return res.body;
    }
    throw new Error('hasVoteQuest failed');
};
export const getBasicQuestInfo = async (questId) => {
    const res = await apiV2.quest.getBasicQuestInfo({
        params: { questId },
    });
    if (res.status === 200) {
        return res.body;
    }
};
export const hasClaimedQuest = async (questId) => {
    const res = await apiV2.quest.hasClaimedQuest({
        params: { questId },
    });
    if (res.status === 200) {
        return res.body;
    }
};
export const hasQuestBeenClaimed = async (questId) => {
    const res = await apiV2.quest.hasQuestBeenClaimed({
        params: { questId },
    });
    if (res.status === 200) {
        return res.body;
    }
};
